import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';
import { reviewsAPI, employerAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  User, Calendar, Clock, FileText, Check, ArrowLeft, ArrowRight, 
  Loader2, RefreshCw, CalendarCheck, X
} from 'lucide-react';

const STEPS = ['employee', 'type', 'datetime', 'focus', 'confirm'];

const REVIEW_TYPES = [
  { id: 'one-time', label: 'One-Time', description: 'Single performance review', color: 'blue', icon: Calendar },
  { id: 'weekly', label: 'Weekly', description: 'Recurring weekly check-in', color: 'green', icon: RefreshCw },
  { id: 'quarterly', label: 'Quarterly', description: 'Every 3 months performance review', color: 'amber', icon: CalendarCheck },
  { id: 'annual', label: 'Annual', description: 'Yearly comprehensive review', color: 'red', icon: FileText },
];

const ScheduleReviewModal = ({ open, onClose, onSuccess, prefilledEmployee = null }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    employee: prefilledEmployee,
    reviewType: '',
    date: '',
    time: '09:00',
    focusAreas: '',
    notes: '',
  });

  useEffect(() => {
    if (open) {
      fetchEmployees();
      if (prefilledEmployee) {
        setFormData(prev => ({ ...prev, employee: prefilledEmployee }));
        setStep(1); // Skip to type selection
      }
    }
  }, [open, prefilledEmployee]);

  useEffect(() => {
    if (!open) {
      // Reset form when modal closes
      setStep(0);
      setFormData({
        employee: null,
        reviewType: '',
        date: '',
        time: '09:00',
        focusAreas: '',
        notes: '',
      });
    }
  }, [open]);

  const fetchEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const res = await employerAPI.getEmployees();
      setEmployees(res.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleNext = () => {
    if (step === 0 && !formData.employee) {
      toast.error('Please select an employee');
      return;
    }
    if (step === 1 && !formData.reviewType) {
      toast.error('Please select a review type');
      return;
    }
    if (step === 2 && (!formData.date || !formData.time)) {
      toast.error('Please select date and time');
      return;
    }
    setStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    if (prefilledEmployee && step === 1) {
      onClose();
      return;
    }
    setStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await reviewsAPI.scheduleReview({
        employee_id: formData.employee.id,
        review_type: formData.reviewType,
        scheduled_date: formData.date,
        scheduled_time: formData.time,
        focus_areas: formData.focusAreas,
        notes: formData.notes,
      });
      
      toast.success('Review scheduled successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error scheduling review:', error);
      toast.error(error.response?.data?.detail || 'Failed to schedule review');
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const name = `${emp.first_name || ''} ${emp.last_name || ''}`.toLowerCase();
    const email = (emp.email || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return name.includes(search) || email.includes(search);
  });

  const getColorClass = (color, variant = 'bg') => {
    const colors = {
      blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/50', text: 'text-blue-400' },
      green: { bg: 'bg-green-500/20', border: 'border-green-500/50', text: 'text-green-400' },
      amber: { bg: 'bg-amber-500/20', border: 'border-amber-500/50', text: 'text-amber-400' },
      red: { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-400' },
    };
    return colors[color]?.[variant] || '';
  };

  const selectedType = REVIEW_TYPES.find(t => t.id === formData.reviewType);

  const renderStep = () => {
    switch (STEPS[step]) {
      case 'employee':
        return (
          <div className="space-y-4">
            <div className="relative">
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-[#16213E]/50 border-[#2A2A4A] text-white rounded-xl h-11"
                data-testid="employee-search-input"
              />
            </div>
            
            <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
              {loadingEmployees ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#E94560]" />
                </div>
              ) : filteredEmployees.length > 0 ? (
                filteredEmployees.map((emp) => (
                  <button
                    key={emp.id}
                    onClick={() => setFormData(prev => ({ ...prev, employee: emp }))}
                    className={`w-full p-3 rounded-xl border text-left transition-all ${
                      formData.employee?.id === emp.id
                        ? 'bg-[#E94560]/20 border-[#E94560]/50'
                        : 'bg-[#16213E]/30 border-[#2A2A4A] hover:border-[#E94560]/30'
                    }`}
                    data-testid={`employee-option-${emp.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {emp.photo_url ? (
                        <img src={emp.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#16213E] flex items-center justify-center">
                          <User className="w-5 h-5 text-[#8888AA]" />
                        </div>
                      )}
                      <div>
                        <p className="text-[#0A0F1E] font-medium">
                          {emp.first_name || emp.last_name 
                            ? `${emp.first_name || ''} ${emp.last_name || ''}`.trim()
                            : emp.email || 'Employee'}
                        </p>
                        <p className="text-[#8888AA] text-sm">{emp.job_title || 'No title'}</p>
                      </div>
                      {formData.employee?.id === emp.id && (
                        <Check className="w-5 h-5 text-[#E94560] ml-auto" />
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8">
                  <User className="w-10 h-10 text-[#8888AA] mx-auto mb-3" />
                  <p className="text-[#8888AA]">No employees found</p>
                  <p className="text-[#8888AA] text-sm mt-1">Create VETT records first to schedule reviews</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'type':
        return (
          <div className="space-y-3">
            {REVIEW_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => setFormData(prev => ({ ...prev, reviewType: type.id }))}
                className={`w-full p-4 rounded-xl border text-left transition-all ${
                  formData.reviewType === type.id
                    ? `${getColorClass(type.color, 'bg')} ${getColorClass(type.color, 'border')}`
                    : 'bg-[#16213E]/30 border-[#2A2A4A] hover:border-[#E94560]/30'
                }`}
                data-testid={`review-type-${type.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg ${getColorClass(type.color, 'bg')} flex items-center justify-center`}>
                    <type.icon className={`w-5 h-5 ${getColorClass(type.color, 'text')}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${formData.reviewType === type.id ? getColorClass(type.color, 'text') : 'text-[#0A0F1E]'}`}>
                      {type.label}
                    </p>
                    <p className="text-[#8888AA] text-sm">{type.description}</p>
                  </div>
                  {formData.reviewType === type.id && (
                    <Check className={`w-5 h-5 ${getColorClass(type.color, 'text')}`} />
                  )}
                </div>
              </button>
            ))}
          </div>
        );

      case 'datetime':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                className="bg-[#16213E]/50 border-[#2A2A4A] text-white rounded-xl h-11"
                data-testid="review-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Time *</Label>
              <Input
                type="time"
                value={formData.time}
                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                className="bg-[#16213E]/50 border-[#2A2A4A] text-white rounded-xl h-11"
                data-testid="review-time-input"
              />
            </div>
            
            {selectedType && formData.reviewType !== 'one-time' && (
              <Card className="bg-[#16213E]/30 border-[#2A2A4A] rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <RefreshCw className={`w-4 h-4 ${getColorClass(selectedType.color, 'text')}`} />
                  <p className="text-[#8888AA] text-sm">
                    This is a <span className={getColorClass(selectedType.color, 'text')}>{selectedType.label}</span> review.
                    Future occurrences will be auto-scheduled upon completion.
                  </p>
                </div>
              </Card>
            )}
          </div>
        );

      case 'focus':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Focus Areas</Label>
              <Textarea
                value={formData.focusAreas}
                onChange={(e) => setFormData(prev => ({ ...prev, focusAreas: e.target.value }))}
                placeholder="e.g., Project completion, communication skills, leadership development..."
                className="bg-[#16213E]/50 border-[#2A2A4A] text-white rounded-xl min-h-[100px]"
                data-testid="focus-areas-input"
              />
              <p className="text-[#8888AA] text-xs">What areas should this review focus on?</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Notes (optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes for this review..."
                className="bg-[#16213E]/50 border-[#2A2A4A] text-white rounded-xl min-h-[80px]"
                data-testid="notes-input"
              />
            </div>
          </div>
        );

      case 'confirm':
        const formattedDate = formData.date 
          ? new Date(formData.date).toLocaleDateString('en-US', { 
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
            })
          : '';
        
        return (
          <div className="space-y-4">
            <Card className="bg-[#16213E]/30 border-[#2A2A4A] rounded-xl p-4">
              <h4 className="text-[#8888AA] text-xs uppercase tracking-wider mb-3">Employee</h4>
              <div className="flex items-center gap-3">
                {formData.employee?.photo_url ? (
                  <img src={formData.employee.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#16213E] flex items-center justify-center">
                    <User className="w-5 h-5 text-[#8888AA]" />
                  </div>
                )}
                <div>
                  <p className="text-[#0A0F1E] font-medium">
                    {formData.employee?.first_name || formData.employee?.last_name 
                      ? `${formData.employee?.first_name || ''} ${formData.employee?.last_name || ''}`.trim()
                      : formData.employee?.email || 'Employee'}
                  </p>
                  <p className="text-[#8888AA] text-sm">{formData.employee?.job_title}</p>
                </div>
              </div>
            </Card>
            
            <Card className="bg-[#16213E]/30 border-[#2A2A4A] rounded-xl p-4">
              <h4 className="text-[#8888AA] text-xs uppercase tracking-wider mb-3">Review Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#8888AA]">Type:</span>
                  <span className={`${selectedType ? getColorClass(selectedType.color, 'text') : 'text-[#0A0F1E]'} font-medium`}>
                    {selectedType?.label}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8888AA]">Date:</span>
                  <span className="text-[#0A0F1E]">{formattedDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8888AA]">Time:</span>
                  <span className="text-[#0A0F1E]">{formData.time}</span>
                </div>
                {formData.focusAreas && (
                  <div className="pt-2 border-t border-[#2A2A4A]">
                    <span className="text-[#8888AA]">Focus Areas:</span>
                    <p className="text-[#0A0F1E] mt-1">{formData.focusAreas}</p>
                  </div>
                )}
              </div>
            </Card>
            
            {formData.reviewType !== 'one-time' && (
              <Card className={`${getColorClass(selectedType?.color || 'blue', 'bg')} border ${getColorClass(selectedType?.color || 'blue', 'border')} rounded-xl p-4`}>
                <div className="flex items-center gap-2">
                  <RefreshCw className={`w-4 h-4 ${getColorClass(selectedType?.color || 'blue', 'text')}`} />
                  <p className={`text-sm ${getColorClass(selectedType?.color || 'blue', 'text')}`}>
                    Next review will be auto-scheduled when this one is completed.
                  </p>
                </div>
              </Card>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const stepTitles = {
    employee: 'Select Employee',
    type: 'Review Type',
    datetime: 'Date & Time',
    focus: 'Focus Areas',
    confirm: 'Confirm & Schedule',
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1A1A2E] border-[#2A2A4A] text-white sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#E94560]" />
            Schedule Review
          </DialogTitle>
        </DialogHeader>
        
        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-4">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  i <= step ? 'bg-[#E94560] text-white' : 'bg-[#16213E] text-[#8888AA]'
                }`}
              >
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-1 rounded ${i < step ? 'bg-[#E94560]' : 'bg-[#2A2A4A]'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        
        <p className="text-[#0A0F1E] font-medium mb-4">{stepTitles[STEPS[step]]}</p>
        
        {/* Step Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {renderStep()}
        </div>
        
        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 mt-4 border-t border-[#2A2A4A]">
          <Button
            variant="ghost"
            onClick={step === 0 ? onClose : handleBack}
            className="text-[#8888AA] hover:text-white"
          >
            {step === 0 ? (
              <>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </>
            )}
          </Button>
          
          {step < STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              className="bg-[#E94560] hover:bg-[#E94560]/90 text-white rounded-xl"
              data-testid="schedule-next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-[#E94560] hover:bg-[#E94560]/90 text-white rounded-xl"
              data-testid="schedule-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Scheduling...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Schedule Review
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleReviewModal;
